// TeacherTimeLog.js (admin)
import React, { useEffect, useState, Fragment } from "react";
import API from "./api";

// ---------- Helpers ----------
function kmToMiles(km, decimals = 2) {
  const n = Number(km);
  if (!isFinite(n)) return null;
  return Number((n * 0.621371).toFixed(decimals));
}
function fmtHMS(dt) {
  if (!dt) return "-";
  const m = String(dt).match(/(\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return "-";
  return `${m[1]}:${m[2]}`;
}
function fmtTimeRange(scheduled_at, event_end_time) {
  if (!scheduled_at) return "";
  const start = (scheduled_at.split(" ")[1] || "").slice(0, 5);
  if (!event_end_time) return start;
  const end = event_end_time.slice(0, 5);
  return `${start}-${end}`;
}
function toDateObj(str) { const d = new Date(str); return isNaN(d) ? null : d; }
function diffMin(a, b) {
  const da = a ? toDateObj(a) : null;
  const db = b ? toDateObj(b) : null;
  if (!da || !db) return null;
  return Math.round((da - db) / 60000);
}
function deltaBadge(mins) {
  if (mins === null) return <span className="text-gray-400">n/a</span>;
  const abs = Math.abs(mins);
  const color = abs <= 2 ? "text-green-600" : abs <= 10 ? "text-yellow-600" : "text-red-600";
  const sign = mins > 0 ? "+" : "";
  return <span className={`font-semibold ${color}`}>{sign}{mins} min</span>;
}
function isSpecialCanceled(visit) {
  const notes = (visit.notes || "").toString();
  return (
    notes.includes("NoShow/withAppointmentConfirmation") ||
    notes.includes("Canceled by BBarent")
  );
}

export default function TeacherTimeLog() {
  const [teachers, setTeachers] = useState([]);
  const [teacherId, setTeacherId] = useState("");
  const [date, setDate] = useState("");
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { API.get('/teachers').then(res => setTeachers(res.data || [])); }, []);
  useEffect(() => {
    API.get('/server-time')
      .then(res => {
        const serverDate = res.data.serverDate || (res.data.serverTime || '').slice(0,10);
        if (serverDate) setDate(serverDate);
      })
      .catch(() => setDate(new Date().toISOString().slice(0,10)));
  }, []);

  useEffect(() => {
    if (!teacherId || !date) { setVisits([]); return; }
    setLoading(true);
    API.get("/time-log", { params: { teacher_id: teacherId, date } })
      .then(res => setVisits(res.data.visits || []))
      .finally(() => setLoading(false));
  }, [teacherId, date]);

  const sorted = (visits || []).slice().sort((a,b) => a.scheduled_at.localeCompare(b.scheduled_at));

  return (
    <div className="max-w-2xl mx-auto pb-10 pt-5 px-2">
      <h2 className="text-lg font-bold mb-5">Teacher Time Logs</h2>

      <div className="mb-4 grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
        <div className="sm:col-span-2">
          <label className="block text-sm mb-1 font-semibold">Teacher</label>
          <select
            className="border px-3 py-2 rounded w-full"
            value={teacherId}
            onChange={e => setTeacherId(e.target.value)}
          >
            <option value="">-- Select Teacher --</option>
            {teachers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm mb-1 font-semibold">Date</label>
          <input
            type="date"
            className="border px-3 py-2 rounded w-full"
            value={date}
            onChange={e => setDate(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="p-6 text-center text-gray-400">Loading...</div>
      ) : !teacherId ? (
        <div className="p-6 text-center text-gray-400">Please select a teacher.</div>
      ) : sorted.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-52 text-gray-400 text-lg">
          <span>No visits.</span>
        </div>
      ) : (
        <div>
          {sorted.map((v, idx) => {
            const tl = v.time_log || {};
            const prev = idx > 0 ? sorted[idx-1] : null;

            const isEvent    = v.type === 'event';
            const isPI       = isEvent && v.program_type === 'PI'; // PI event
            const isCanceled = v.status === "canceled";
            const allowInterSlot = !isCanceled || isSpecialCanceled(v);

            // Prva vožnja se prikazuje:
            // - za sve non-PI evente (kao do sada), ili
            // - ako je explicitan flag pay_first_commute = 1
            // Za PI evente se prva vožnja prikazuje SAMO ako je pay_first_commute = 1
            const canShowFirstLeg =
              idx === 0 && (
                (!isPI && isEvent) ||
                v.pay_first_commute === 1
              );

            const showInterSlot =
              allowInterSlot && (Boolean(prev) || canShowFirstLeg);

            const dIn  = tl.subjective_in_time  ? diffMin(tl.subjective_in_time, v.checkin_time) : null;
            const dOut = tl.subjective_out_time ? diffMin(tl.subjective_out_time, v.checkout_time) : null;

            return (
              <Fragment key={v.id}>
                {/* Inter-slot (VOŽNJA I PAUZA) od PRETHODNE vizite ka OVOJ viziti */}
                {showInterSlot && (
                  <div className="rounded-2xl border bg-gray-50 p-3 mb-4">
                    <div className="text-xs tracking-wide text-gray-500 mb-2">
                      Drive & break from{" "}
                      <span className="font-semibold">
                        {prev ? `${prev.student_first_name} ${prev.student_last_name}` : "home"}
                      </span>{" "}
                      →{" "}
                      <span className="font-semibold">
                        {v.student_first_name} {v.student_last_name}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <div className="text-[11px] text-gray-500">Drive Start</div>
                        <div className="font-mono">
                          {fmtHMS((tl.drive_start_time || "").split(" ")[1] || "")}
                        </div>
                      </div>
                      <div>
                        <div className="text-[11px] text-gray-500">Drive End</div>
                        <div className="font-mono">
                          {fmtHMS((tl.drive_end_time || "").split(" ")[1] || "")}
                        </div>
                      </div>
                      <div>
                        <div className="text-[11px] text-gray-500">Miles (teacher)</div>
                        <div className="font-mono">{tl.miles_driven ?? "-"}</div>
                      </div>

                      {/* Break se NE prikazuje ako je prvi commute koji se ne plaća */}
                      {(prev || !canShowFirstLeg) && (
                        <div>
                          <div className="text-[11px] text-gray-500">Paid Break (min)</div>
                          <div className="font-mono">{tl.break_minutes ?? "-"}</div>
                        </div>
                      )}

                      {/* NEW: Extra cost */}
                      <div>
                        <div className="text-[11px] text-gray-500">Extra cost ($)</div>
                        <div className="font-mono">
                          {tl.extra_cost != null ? Number(tl.extra_cost).toFixed(2) : "-"}
                        </div>
                      </div>

                      <div className="col-span-2">
                        <div className="text-[11px] text-gray-500">Route (objective, ORS)</div>
                        <div className="font-mono text-sm">
                          {tl.objective_route_distance_km != null || tl.objective_route_duration_min != null
                            ? (() => {
                                const km = tl.objective_route_distance_km != null ? Number(tl.objective_route_distance_km) : null;
                                const mi = km != null ? kmToMiles(km) : null;
                                const mins = tl.objective_route_duration_min ?? "?";
                                return `${mi ?? "?"} mi • ${mins} min`;
                              })()
                            : "-"}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Sam slot vizite (isti vizuelni stil kao kod teachera) */}
                <div
                  className={`rounded-2xl shadow p-4 mb-6 ${
                    v.status === "canceled"
                      ? "bg-purple-50"                      // canceled = ljubičasto
                      : isPI
                      ? "border-l-4 border-orange-400 bg-orange-50" // PI event = blago narandžasto
                      : isEvent
                      ? "border-l-4 border-yellow-400 bg-yellow-50" // ostali eventi = žuto
                      : "bg-green-50"
                  }`}
                >
                  <div className="flex justify-between items-center mb-2">
                    <div className="font-semibold text-base">
                      {v.student_first_name} {v.student_last_name}
                    </div>
                    <div className="font-mono text-lg">
                      {fmtTimeRange(v.scheduled_at, v.event_end_time)}
                    </div>

                    {isPI ? (
                      <span className="ml-2 inline-flex items-center text-orange-700 text-xs font-bold">
                        <svg className="inline-block mr-1" width={18} height={18} viewBox="0 0 24 24">
                          <path fill="currentColor" d="M7 9v6h2V9zm4 0v6h2V9zm4 0v6h2V9z"/>
                        </svg>
                        PI
                      </span>
                    ) : isEvent && (
                      <span className="ml-2 inline-flex items-center text-yellow-700 text-xs font-bold">
                        <svg className="inline-block mr-1" width={18} height={18} viewBox="0 0 24 24">
                          <path fill="currentColor" d="M7 9v6h2V9zm4 0v6h2V9zm4 0v6h2V9z"/>
                        </svg>
                        Event
                      </span>
                    )}

                    {v.status === "canceled" && (
                      <span
                        className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full
                                   bg-purple-100 text-purple-700 text-xs font-semibold tracking-wide"
                      >
                        Canceled
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {/* Objektivno */}
                    <div>
                      <div className="text-[11px] text-gray-500">Check-in (objective)</div>
                      <div className="font-mono">
                        {fmtHMS((v.checkin_time || "").split(" ")[1] || "")}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] text-gray-500">Check-out (objective)</div>
                      <div className="font-mono">
                        {fmtHMS((v.checkout_time || "").split(" ")[1] || "")}
                      </div>
                    </div>

                    {/* Subjektivno (teacher) – read-only u adminu */}
                    <div>
                      <div className="text-[11px] text-gray-500">Time-in (teacher)</div>
                      <div className="font-mono">
                        {fmtHMS((tl.subjective_in_time || "").split(" ")[1] || "")}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] text-gray-500">Time-out (teacher)</div>
                      <div className="font-mono">
                        {fmtHMS((tl.subjective_out_time || "").split(" ")[1] || "")}
                      </div>
                    </div>
                  </div>

                  {/* Delta prikaz */}
                  <div className="mt-2 text-sm">
                    <span className="text-gray-500 mr-2">Δ in:</span>{deltaBadge(dIn)}
                    <span className="text-gray-500 mx-2">•</span>
                    <span className="text-gray-500 mr-2">Δ out:</span>{deltaBadge(dOut)}
                  </div>
                </div>
              </Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}








